import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AnalyticsTracker, useAnalytics } from '@backstage/core-plugin-api';

import { LoadingOverlay, useAuth } from '@spotify/confidence-core-react';
import { useCollaborativeDocument } from '@spotify/confidence-editor-react';
import { useAccounts } from '@spotify/plugin-confidence-app-react';

import { navigateToUrl, urlFromResource } from './utils';

const notificationClicked = (
  params: URLSearchParams,
  analytics: AnalyticsTracker,
) => {
  const activityId = params.get('activity');
  if (activityId) {
    analytics.captureEvent('notification-clicked', 'redirect', {
      attributes: {
        activityId: activityId,
      },
    });
  }
};

const COLLABORATIVE_DOCUMENTS_PREFIX = 'collaborativeDocuments/';

const RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts, selectedAccountId, isLoading, isSwitching, switchAccount } =
    useAccounts();

  const {
    isAuthenticated,
    isLoading: authIsLoading,
    loginWithRedirect,
  } = useAuth();
  const analytics = useAnalytics();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const resource = params.get('resource');
  const account = params.get('account');
  const hasAccountMembership = accounts.some(a => a.accountId === account);
  const isCollaborativeDocument =
    resource?.startsWith(COLLABORATIVE_DOCUMENTS_PREFIX) ?? false;

  const authReady = !authIsLoading && isAuthenticated;
  const accountReady =
    !isLoading && (account === null || account === selectedAccountId);

  const {
    collaborativeDocument,
    isLoading: collabDocLoading,
    error: collabDocError,
  } = useCollaborativeDocument({
    name: resource ?? '',
    skip: !isCollaborativeDocument || !authReady || !accountReady,
  });

  useEffect(() => {
    if (!resource) return;

    const currentRedirectUrl = `${location.pathname}${location.search}`;

    if (!authIsLoading && !isAuthenticated) {
      const returnTo = isCollaborativeDocument
        ? currentRedirectUrl
        : urlFromResource(resource);
      notificationClicked(params, analytics);
      loginWithRedirect({ appState: { returnTo } });
      return;
    }

    if (
      account &&
      account !== selectedAccountId &&
      !isLoading &&
      !isSwitching
    ) {
      if (!authReady) return;
      if (!hasAccountMembership) {
        navigate('/', { replace: true });
        return;
      }

      const returnTo = isCollaborativeDocument
        ? currentRedirectUrl
        : urlFromResource(resource);
      notificationClicked(params, analytics);
      switchAccount(account, returnTo);
      return;
    }

    if (!isCollaborativeDocument) {
      if (account === null || account === selectedAccountId) {
        const newUrl = urlFromResource(resource);
        notificationClicked(params, analytics);
        navigateToUrl(newUrl);
      }
      return;
    }

    if (!authReady || !accountReady) return;

    if (collabDocLoading) return;

    if (collabDocError || !collaborativeDocument?.resource) {
      navigate('/', { replace: true });
      return;
    }

    notificationClicked(params, analytics);
    navigateToUrl(`/${collaborativeDocument.resource}/report`);
  }, [
    selectedAccountId,
    isLoading,
    isSwitching,
    switchAccount,
    isAuthenticated,
    authIsLoading,
    loginWithRedirect,
    isCollaborativeDocument,
    collaborativeDocument,
    collabDocLoading,
    collabDocError,
    authReady,
    accountReady,
    params,
    resource,
    account,
    hasAccountMembership,
    analytics,
    location,
    navigate,
  ]);

  return <LoadingOverlay loading />;
};

export default RedirectHandler;
